import React, { useState, useRef } from 'react';

interface ReaderFlipViewProps {
    pages: string[][];
    pageIndex: number;
    verseData: Record<string, string>;
    onNext: () => void;

    onPrev: () => void;
    isPaginating: boolean;
    showVerseNumbers?: boolean; // New prop
    isSpeaking?: boolean;
    onSpeakToggle?: (text: string[]) => void;
    onGoToLibrary?: () => void;
}

export const ReaderFlipView: React.FC<ReaderFlipViewProps> = ({
    pages,
    pageIndex,
    verseData,
    onNext,
    onPrev,
    isPaginating,
    showVerseNumbers,
    isSpeaking,
    onSpeakToggle,
    onGoToLibrary
}) => {
    // ... (rest of hook logic)
    // Track direction for animation
    const [navDirection, setNavDirection] = useState<'next' | 'prev' | 'none'>('none');

    // Trigger explicit navigation
    const triggerNext = () => {
        setNavDirection('next');
        onNext();
    };

    const triggerPrev = () => {
        setNavDirection('prev');
        onPrev();
    };

    // Touch handling for swipe
    const touchStart = useRef<number | null>(null);

    const currentPageVerses = pages[pageIndex] || [];

    const handleTouchStart = (e: React.TouchEvent) => {
        touchStart.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (touchStart.current === null) return;
        const diff = touchStart.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) { // Threshold
            if (diff > 0) triggerNext(); // Swipe left -> Next
            else triggerPrev(); // Swipe right -> Prev
        }
        touchStart.current = null;
    };

    // Click zones
    const handleClick = (e: React.MouseEvent) => {
        const width = e.currentTarget.clientWidth;
        const x = e.nativeEvent.offsetX;
        if (x < width * 0.3) triggerPrev();
        else if (x > width * 0.7) triggerNext();
    };

    const handleSpeakClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!onSpeakToggle) return;
        const text = currentPageVerses.map(v => verseData[v]);
        onSpeakToggle(text);
    };

    if (isPaginating || !verseData) {
        return (
            <div style={{ padding: '20px', textAlign: 'center', marginTop: '20vh' }}>
                <p>Loading page...</p>
                {onGoToLibrary && (
                    <button onClick={onGoToLibrary} style={{
                        marginTop: '20px', padding: '10px 20px',
                        background: 'var(--surface-hover)', border: '1px solid var(--border-color)',
                        borderRadius: '8px', color: 'var(--text-color)', cursor: 'pointer'
                    }}>
                        Go to My Library
                    </button>
                )}
            </div>
        );
    }

    if (pages.length === 0) return <div style={{ padding: '20px' }}>End of content.</div>;

    // Safety check if index out of bounds
    if (!currentPageVerses.length) {
        // Should ideally not happen or handled by parent
        return <div style={{ padding: '20px' }}></div>;
    }

    // Animation Styles
    const containerStyle: React.CSSProperties = {
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        userSelect: 'none'
    };

    const pageStyle: React.CSSProperties = {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        padding: '1rem',
        paddingBottom: '80px', // Space for controls
        boxSizing: 'border-box',
        overflowY: 'auto',
        background: 'var(--bg-color)',
        transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
        boxShadow: '0 0 20px rgba(0,0,0,0.05)'
    };

    const animClass = navDirection === 'next' ? 'slide-in-right' : navDirection === 'prev' ? 'slide-in-left' : '';

    return (
        <div
            style={containerStyle}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onClick={handleClick}
        >
            <style>{`
                @keyframes slideInRight {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                @keyframes slideInLeft {
                    from { transform: translateX(-100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                .slide-in-right { animation: slideInRight 0.3s forwards; }
                .slide-in-left { animation: slideInLeft 0.3s forwards; }
                
                .flip-verse { margin-bottom: 1em; line-height: 1.7; font-size: 1.1em; }
                .flip-num { color: var(--text-secondary); margin-right: 6px; font-size: 0.8em; vertical-align: top; }
            `}</style>

            <div key={pageIndex} className={animClass} style={pageStyle}>
                {currentPageVerses.map(vNum => (
                    <p key={vNum} className="flip-verse">
                        {showVerseNumbers && <sup className="flip-num">{vNum}</sup>}
                        {verseData[vNum]}
                    </p>
                ))}

                <div style={{ marginTop: '40px', textAlign: 'center', fontSize: '0.8rem', opacity: 0.7 }}>
                    Page {pageIndex + 1} / {pages.length}
                </div>
            </div>
        </div>
    );
};

